import {
  type RouterOutput,
  ReactQueryV5,
} from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { endOfWeek, getUnixTime, startOfWeek } from "date-fns";
import CONSTANTS from "core/constants";
import { useWeekDate } from "./useWeekDate";

type RawServiceWindow =
  RouterOutput["serviceWindows"]["listInTimeFrame"][number];

export type ServiceWindow = {
  serviceWindowId: number;
  /** Unix seconds, to match the calendar's start/end accessors. */
  startsAt: number;
  endsAt: number;
  reason: string | null;
};

const selectServiceWindows = (windows: RawServiceWindow[]): ServiceWindow[] =>
  windows.map((window) => ({
    serviceWindowId: window.serviceWindowId,
    startsAt: getUnixTime(new Date(window.startsAt)),
    endsAt: getUnixTime(new Date(window.endsAt)),
    reason: window.reason,
  }));

/**
 * Fetches the service windows overlapping the currently viewed week so the
 * calendar can show psychologists which time is blocked for new slots.
 */
export const useServiceWindows = () => {
  const api = useMindlerApi();
  const { date: weekDate } = useWeekDate();

  return useQuery(
    api.serviceWindows.listInTimeFrame.queryOptions(
      {
        fromDate: startOfWeek(weekDate),
        toDate: endOfWeek(weekDate),
      },
      {
        refetchInterval: CONSTANTS.FETCH_INTERVAL,
        select: selectServiceWindows,
      },
    ),
  );
};

/** True if [startsAt, endsAt) (unix seconds) overlaps any service window. */
export const overlapsServiceWindow = (
  windows: ServiceWindow[],
  startsAt: number,
  endsAt: number,
): boolean =>
  windows.some(
    (window) => startsAt < window.endsAt && endsAt > window.startsAt,
  );

/** Returns the service window covering the given instant (unix seconds), if any. */
export const serviceWindowAt = (
  windows: ServiceWindow[],
  unixSeconds: number,
): ServiceWindow | undefined =>
  windows.find(
    (window) => unixSeconds >= window.startsAt && unixSeconds < window.endsAt,
  );
